'use client'

import React, { useEffect, useState, useRef, useCallback } from "react";
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@/redux/store';
import { showErrorToast, showSuccessToast } from "../toast/toast";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import Footer from "../footer";
import AccordionForm, { AccordionFormRef } from "../accordion-form";
import PrimaryPakagingDetails from "./components/primary-pakaging-details";
import SecondTertiaryPakagingDetails from "./components/second-tertiary-pakaging-details";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PalletDetails from "./components/pallet-details";
import UnitTesting from "./components/unit-testing";
import { ChevronsLeft, ChevronsRight, Loader2, Trash2 } from 'lucide-react'
import PaletizingRecipe from "./components/paletizing-recipe";
import secondaryImage from '../../pallet-config/secondaryPallet.jpg'
import tertiaryImage from '../../pallet-config/tertiaryPallet.jpg'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { InfoIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AddPalletDialog from "../add-pallet-dialog";
import {
  useGetPalletsQuery,
  useGetPalletDetailsQuery,
  useCreatePalletMutation,
  useUpdatePalletMutation,
  useCreatePalletDetailsMutation,
  useDeletePalletMutation
} from '@/redux/slices/pallet/palletApiSlice';
import { setPalletTypes, addPalletType, updatePalletType, setActivePallet, removePalletType } from '@/redux/slices/pallet/palletSlice';
import { createdPalletFromResponse, resolveActivePallet } from '@/lib/pallets';
import { ChevronDown, ChevronRight } from "lucide-react";
import { Checkbox } from "../ui/checkbox";
import { PalletType } from "@/redux/slices/pallet/palletSlice";
import axios from 'axios';
import SecureStorage from "@/services/SecureStorage";
import { useValidatePalletConfigQuery } from "@/redux/slices/define/defineApiSlice";
import { useNavigate, useLocation } from 'react-router-dom';
import { Skeleton } from "@/components/ui/skeleton";
import { showWarningToast } from '@/components/toast/toast';
import { withCalculatedUnitLoadTotals } from '@/lib/palletCalculations';
const validationSchema = Yup.object({
  primaryPackagingDetails: Yup.object({
    packageType: Yup.string().required(),
    category: Yup.string().required(),
    brand: Yup.string().required(),
    application: Yup.string().required(),
    finishType: Yup.string().when('packageType', {
      is: (value: any) => value === 'bottle',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable(),
    }).required(),
    packageMaterial: Yup.string().required(),
    filledBottleTopLoad: Yup.number().nullable(),
    diameter: Yup.number().required(),
    packageHeight: Yup.number().required(),
    netContent: Yup.number().required(),
    filledWeight: Yup.number().required(),
    bottleDesign: Yup.string().nullable(),
    canDesign: Yup.string().nullable(),
  }),

  secondaryPackagingDetails: Yup.object({
    secondaryPkg: Yup.string().nullable(),
    secondaryPackageType: Yup.string().when('secondaryPkg', {
      is: (value: any) => value === 'true',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable(),
    }).required(),
    secondaryPackageCase: Yup.string().when('secondaryPkg', {
      is: (value: any) => value === 'true',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable(),
    }).required(),
    secondaryUnitNetWeight: Yup.number().when('secondaryPkg', {
      is: (value: any) => value === 'true',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable(),
    }).required(),
    secondaryUnitGrossWeight: Yup.number().when('secondaryPkg', {
      is: (value: any) => value === 'true',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable(),
    }).required(),
    secondaryCaseLength: Yup.number().when('secondaryPkg', {
      is: (value: any) => value === 'true',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable(),
    }).required(),
    secondaryCaseWidth: Yup.number().when('secondaryPkg', {
      is: (value: any) => value === 'true',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable(),
    }).required(),
    secondaryCaseHeight: Yup.number().when('secondaryPkg', {
      is: (value: any) => value === 'true',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable(),
    }).required(),
    filledCaseCompression: Yup.number().when('secondaryPkg', {
      is: (value: any) => value === 'true',
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.nullable(),
    }),
  }),

  tertiaryPackagingDetails: Yup.object({
    tertiaryPkg: Yup.string().nullable(),
    tertiaryPackageType: Yup.string().nullable(),
    tertiarySecondaryPackagesPerUnit: Yup.string().nullable(),
    tertiaryUnitNetWeight: Yup.number().nullable(),
    tertiaryUnitGrossWeight: Yup.number().nullable(),
    tertiaryCaseLength: Yup.number().nullable(),
    tertiaryCaseWidth: Yup.number().nullable(),
    tertiaryCaseHeight: Yup.number().nullable(),
  }),

  palletDetails: Yup.object({
    // palletLength: Yup.number().nullable(),
    // palletWidth: Yup.number().nullable(),
    // palletDepth: Yup.number().nullable(),
    // palletMaterial: Yup.string().nullable(),
    palletTypes: Yup.string().required()
  }),

  unitLoadDetails: Yup.object({
    unitLoadLength: Yup.number().required(),
    unitLoadWidth: Yup.number().required(),
    unitLoadHeight: Yup.number().required(),
    unitLoadWeight: Yup.number().required(),
    unitCasesPerLayer: Yup.number().required(),
    unitLayersPerUnitLoad: Yup.number().required(),
    machineAssembled: Yup.string().nullable(),
    plant: Yup.string().nullable(),
    plantLine: Yup.string().nullable(),
    newPlantLine: Yup.string().nullable(),
    engHelp: Yup.string().nullable(),
    varietyPack: Yup.string().nullable(),
    totalCasesPerUnitLoad: Yup.number().nullable(),
    itemsPerPallet: Yup.number().nullable(),
    palletPatternDesign: Yup.string().required(),
  }),

  palletizingRecipe: Yup.object({
    stretchGa: Yup.string().required(),
    stretchWidth: Yup.number().required(),
    numberOfRevsTop: Yup.number().required(),
    numberOfRevsMiddle: Yup.number().required(),
    numberOfRevsBottom: Yup.number().required(),
    cutAndWeigh: Yup.string().nullable(),
    preStretch: Yup.number().nullable(),
    stretchWrapSpecNumber: Yup.string().nullable()
  }),
});

interface PalletConfigProps {
  projectId?: string;
  isOffline?: boolean;
  onOfflineSubmit?: (values: any) => Promise<void>;
  getOfflinePalletDetails?: (palletId: number) => Promise<void>;
}

export default function PalletConfig({
  isOffline,
  onOfflineSubmit,
  getOfflinePalletDetails
}: PalletConfigProps) {
  const dispatch = useDispatch();
  const { projectId } = useSelector((state: RootState) => state.define);
  const { shippingModes } = useSelector((state: RootState) => state.define);
  const { activePallet, palletTypes } = useSelector((state: RootState) => state.pallets);
  const [createPalletDetails, { isLoading: createPalletDetailsLoading }] = useCreatePalletDetailsMutation();
  const [createPallet, { isLoading: createPalletLoading }] = useCreatePalletMutation();
  const [updatePallet] = useUpdatePalletMutation();
  const { data: pallets, isFetching, refetch: refetchPallets } = useGetPalletsQuery(projectId ?? '', {
    skip: !projectId || isOffline,
    refetchOnMountOrArgChange: true
  });
  const [deleteLoading, setDeleteLoading] = useState(false)
  const { data: palletDetails, isFetching: isPalletDetailsLoading, refetch: refetchPalletDetails } = useGetPalletDetailsQuery(activePallet?.id, { refetchOnMountOrArgChange: true });
  const [offlinePalletDetails, setOfflinePalletDetails] = useState<any>(null)
  const [expandedPallets, setExpandedPallets] = useState<{ [key: number]: boolean }>({});
  const [activeComponent, setActiveComponent] = useState<string | null>(null);
  const accordionRefs = useRef<{ [key: string]: React.RefObject<AccordionFormRef> }>({});
  const pathname = useLocation();
  const [isNav, setIsNav] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [palletToDelete, setPalletToDelete] = useState<any>(null);
  const [deletePallet, { isLoading: isDeletePalletLoading }] = useDeletePalletMutation();

  useEffect(() => {
    if (pallets && !isOffline) {
      dispatch(setPalletTypes(pallets.pallets));
    }
  }, [pallets, dispatch, isOffline]);

  const formik = useFormik({
    initialValues: {
      primaryPackagingDetails: {
        packageType: '',
        category: '',
        brand: '',
        application: '',
        finishType: '',
        packageMaterial: '',
        filledBottleTopLoad: '',
        diameter: '',
        diameterUnit: 'inch',
        packageHeight: '',
        packageHeightUnit: 'inch',
        isFilledWeightAutoCalculated: true,
        netContent: '',
        netContentUnit: 'oz',
        filledWeight: '',
        filledWeightUnit: 'lbs',
        bottleDesign: '',
        canDesign: ''
      },
      secondaryPackagingDetails: {
        secondaryPkg: "false", isUnitNetWeightAutoCalculated: true, isUnitGrossWeightAutoCalculated: true,
        isCaseLengthAutoCalculated: true,
        isCaseWidthAutoCalculated: true,
        isCaseHeightAutoCalculated: true

      },
      tertiaryPackagingDetails: {
        tertiaryPkg: "false",
        isTerUnitNetWeightAutoCalculated: true,
        isTerUnitGrossWeightAutoCalculated: true,
        isTerCaseLengthAutoCalculated: true,
        isTerCaseWidthAutoCalculated: true,
        isTerCaseHeightAutoCalculated: true
      },
      palletDetails: {},
      unitLoadDetails: {
        isUnitLoadLengthAutoCalculated: true,
        isUnitLoadWidthAutoCalculated: true,
        isUnitLoadHeightAutoCalculated: true,
        isUnitLoadWeightAutoCalculated: true,
        isCasesPerLayerAutoCalculated: true
      },
      palletizingRecipe: {},
    },
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        const valuesWithTotals = withCalculatedUnitLoadTotals(values);
        if (isOffline && onOfflineSubmit) {
          await onOfflineSubmit({
            type: 'update',
            palletId: activePallet?.id,
            ...valuesWithTotals
          });
        } else {

          const dataToSend = {
            ...valuesWithTotals,
            secondaryPackagingDetails: valuesWithTotals.secondaryPackagingDetails.secondaryPkg === "true"
              ? valuesWithTotals.secondaryPackagingDetails
              : { secondaryPkg: false },
            tertiaryPackagingDetails: valuesWithTotals.tertiaryPackagingDetails.tertiaryPkg === "true"
              ? valuesWithTotals.tertiaryPackagingDetails
              : { tertiaryPkg: false },
          };
          console.log({
            type: palletDetails ? "update" : "create",
            palletId: activePallet?.id,
            ...dataToSend,
          }, 'neww dataa is here')
          await createPalletDetails({
            type: palletDetails ? "update" : "create",
            palletId: activePallet?.id,
            ...dataToSend,
          }).unwrap();
          showSuccessToast("Pallet details saved successfully");
        }

        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Error saving pallet details:', error);
        showErrorToast("Failed to save pallet details");
      }
    }
  });
  console.log(activePallet, 'activePalletactivePallet')
  useEffect(() => {
    const nextActivePallet = resolveActivePallet(activePallet, palletTypes);
    if (nextActivePallet) {
      dispatch(setActivePallet(nextActivePallet));
    }
  }, [palletTypes, activePallet, dispatch]);
  console.log(palletDetails?.pallet, 'palletDetailsss')
  useEffect(() => {
    if (palletDetails && palletDetails !== undefined) {
      const newValues = {
        primaryPackagingDetails: palletDetails.pallet.primaryPackagingDetails[0] || formik.initialValues.primaryPackagingDetails,
        secondaryPackagingDetails: palletDetails.pallet.secondaryPackagingDetails[0] && palletDetails.pallet.secondaryPackagingDetails[0].secondaryPkg
          ? { ...palletDetails.pallet.secondaryPackagingDetails[0], secondaryPkg: "true" }
          : formik.initialValues.secondaryPackagingDetails,
        tertiaryPackagingDetails: palletDetails.pallet.tertiaryPackagingDetails[0] && palletDetails.pallet.tertiaryPackagingDetails[0].tertiaryPkg
          ? { ...palletDetails.pallet.tertiaryPackagingDetails[0], tertiaryPkg: "true" }
          : formik.initialValues.tertiaryPackagingDetails,
        palletDetails: palletDetails.pallet.palletDetails[0] || formik.initialValues.palletDetails,
        unitLoadDetails: palletDetails.pallet.unitLoadDetails[0] || formik.initialValues.unitLoadDetails,
        palletizingRecipe: palletDetails.pallet.palletizingRecipe[0] || formik.initialValues.palletizingRecipe,
      };
      formik.setValues(newValues);
      setTimeout(() => formik.validateForm(), 0);
      setHasUnsavedChanges(false);
    }
  }, [palletDetails]);
  useEffect(() => {
    if (offlinePalletDetails && offlinePalletDetails !== undefined && offlinePalletDetails !== null) {
      const newValues = {
        primaryPackagingDetails: offlinePalletDetails.primaryPackagingDetails[0] || formik.initialValues.primaryPackagingDetails,
        secondaryPackagingDetails: offlinePalletDetails.secondaryPackagingDetails[0] && offlinePalletDetails.secondaryPackagingDetails[0].secondaryPkg
          ? { ...offlinePalletDetails.secondaryPackagingDetails[0], secondaryPkg: "true" }
          : formik.initialValues.secondaryPackagingDetails,
        tertiaryPackagingDetails: offlinePalletDetails.tertiaryPackagingDetails[0] && offlinePalletDetails.tertiaryPackagingDetails[0].tertiaryPkg
          ? { ...offlinePalletDetails.tertiaryPackagingDetails[0], tertiaryPkg: "true" }
          : formik.initialValues.tertiaryPackagingDetails,
        palletDetails: offlinePalletDetails.palletDetails[0] || formik.initialValues.palletDetails,
        unitLoadDetails: offlinePalletDetails.unitLoadDetails[0] || formik.initialValues.unitLoadDetails,
        palletizingRecipe: offlinePalletDetails.palletizingRecipe[0] || formik.initialValues.palletizingRecipe,
      };
      formik.setValues(newValues);
      setTimeout(() => formik.validateForm(), 0);
      setHasUnsavedChanges(false);
    }
  }, [offlinePalletDetails]);
  console.log(offlinePalletDetails, 'offlinePalletDetails')
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const handleAddPalletDialog = async (palletData: any) => {
    try {
      if (isOffline && onOfflineSubmit) {
        await onOfflineSubmit({
          type: 'create',
          palletDetails: {
            projectId: Number(projectId),
            ...palletData
          },
          shippingModeIds: palletData.shippingModes
        });
        console.log({
          type: 'create',
          palletDetails: {
            projectId: Number(projectId),
            ...palletData
          },
          shippingModeIds: palletData.shippingModes
        }, 'neww dataa is here')
      } else {
        const response = await createPallet({
          type: 'create',
          palletDetails: {
            projectId: Number(projectId),
            ...palletData
          },
          shippingModeIds: palletData.shippingModes
        }).unwrap();
        console.log({
          type: 'create',
          palletDetails: {
            projectId: Number(projectId),
            ...palletData
          },
          shippingModeIds: palletData.shippingModes
        }, 'neww dataa is here')

        const createdPallet = createdPalletFromResponse(response);
        if (createdPallet) {
          dispatch(addPalletType({ ...createdPallet, lpnNumbers: [] }));
        }
        showSuccessToast("New Pallet Added and Generated");
        refetchPallets();
      }
    } catch (error) {
      // showErrorToast("Failed to add pallet");
      console.error("Error adding pallet:", error);
    }
  };
  console.log(formik.values, 'formik.values')
  const handleUpdatePallet = async (palletData: any) => {
    try {
      if (isOffline && onOfflineSubmit) {
        await onOfflineSubmit({
          type: 'updatePallet',
          palletId: palletData.id,
          ...palletData
        });
      } else {
        const response = await createPallet({
          type: "update",
          palletId: palletData.id,
          palletDetails: {
            variant: palletData.variant,
            name: palletData.name,
            numberOfPallets: palletData.numberOfPallets,
            projectId: palletData.projectId
          },
          shippingModeIds: palletData.shippingModes.map((sm: any) => sm.id),
        }).unwrap();

        // dispatch(setActivePallet(response.pallet));
        showSuccessToast("Pallet has been Updated");
        refetchPallets();
      }
    } catch (error) {
      showErrorToast("Failed to update pallet");
      console.error("Error updating pallet:", error);
    }
  };

  const accordion = [
    {
      title: "Primary Packaging Details",
      form: <PrimaryPakagingDetails setHasUnsavedChanges={setHasUnsavedChanges} formik={formik} />,
      tooltipContent: "Tooltip For Primary Packaging Details",
    },
    {
      title: "Secondary and Tertiary Packaging Details",
      form: <SecondTertiaryPakagingDetails formik={formik} setHasUnsavedChanges={setHasUnsavedChanges} />,
      tooltipContent: "Tooltip For Secondary and Tertiary Packaging Details",
    },
    {
      title: "Pallet Details",
      form: <PalletDetails formik={formik} />,
      tooltipContent: "Tooltip For Pallet Details",
    },
    {
      title: "Unit Load Details",
      form: <UnitTesting formik={formik} setHasUnsavedChanges={setHasUnsavedChanges} />,
      tooltipContent: "Tooltip For Unit Load Details",
    },
    {
      title: "Palletizing Recipe",
      form: <PaletizingRecipe formik={formik} />,
      tooltipContent: "Tooltip For Palletizing Recipe",
    },
  ];

  useEffect(() => {
    accordion.forEach((item) => {
      accordionRefs.current[item.title] = React.createRef<AccordionFormRef>();
    });
  }, []);
  useEffect(() => {
    console.log(palletTypes, 'palletTypes')
    if (activePallet && isOffline) {
      if (getOfflinePalletDetails) {
        const test = async () => {
          const offlinedata = await getOfflinePalletDetails(Number(activePallet.id))
          console.log(offlinedata, 'offlinedata')
          setOfflinePalletDetails(offlinedata)
        }
        test()
      }
    }
  }, [activePallet, isOffline])
  const togglePalletExpansion = (palletId: number) => {
    setExpandedPallets(prev => ({ ...prev, [palletId]: !prev[palletId] }));
  };

  const handleComponentClick = (component: string) => {
    setActiveComponent(component);
    const ref = accordionRefs.current[component];
    if (ref && ref.current) {
      ref.current.scrollIntoView();
      ref.current.expand();
    }
  };

  const handlePalletChange = async (newPalletId: string) => {
    console.log(newPalletId, 'newPalletId is here')
    if (hasUnsavedChanges && !isOffline) {
      console.log('submission happening')
      await handleSubmit(formik.values)
    }
    if (palletTypes.length > 0) {
      dispatch(setActivePallet(palletTypes.find(p => p?.id?.toString() === newPalletId) || null));
    }
  };

  const handleFormChange = (event: React.FormEvent<HTMLFormElement>) => {
    // React bubbles changes from portaled dialogs (e.g. Add Pallet) through this form;
    // only edits to the pallet details themselves are unsaved changes.
    if (!event.currentTarget.contains(event.target as Node)) return;
    setHasUnsavedChanges(true);
    formik.validateForm();
  };


  const handleSubmit = async (values: any) => {
    const valuesWithTotals = withCalculatedUnitLoadTotals(values);
    if (isOffline && onOfflineSubmit) {
      await onOfflineSubmit(valuesWithTotals);
      return;
    }

    try {
      if (activePallet?.id) {
        const dataToSend = {
          ...valuesWithTotals,
          secondaryPackagingDetails: valuesWithTotals.secondaryPackagingDetails.secondaryPkg === "true"
            ? valuesWithTotals.secondaryPackagingDetails
            : { secondaryPkg: false },
          tertiaryPackagingDetails: valuesWithTotals.tertiaryPackagingDetails.tertiaryPkg === "true"
            ? valuesWithTotals.tertiaryPackagingDetails
            : { tertiaryPkg: false },
        };
        await createPalletDetails({
          type: palletDetails ? "update" : "create",
          palletId: activePallet?.id,
          ...dataToSend,
        }).unwrap();

        showSuccessToast("Pallet Details have been saved");
        setHasUnsavedChanges(false);

        // Validate after saving
        // await validatePalletConfig();
      }
    } catch (error) {
      showErrorToast("Failed to save Pallet Details");
      console.error("Error in CreatePalletDetails:", error);
    }
  };

  // Force validation on initial load
  useEffect(() => {
    formik.validateForm();
  }, []);

  // Force validation when active pallet changes
  // useEffect(() => {
  //   if (activePallet) {
  //     formik.validateForm();
  //   }
  // }, [activePallet]);

  // Create a component for the accordion skeletons to reuse
  const AccordionSkeletons = () => (
    <div className="w-full space-y-4 pb-20 x-space">
      <h1 className="semi-bold-20 pt-8">
        <Skeleton className="h-8 w-40" />
      </h1>

      <div className="w-full flex-center gap-2 md:gap-4 mb-6">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex flex-col gap-2 w-full">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        ))}
      </div>

      {/* Accordion Skeletons */}
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="mb-8 border rounded-md">
          <div className="p-4 border-b">
            <Skeleton className="h-6 w-48" />
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-6">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="flex flex-col gap-2">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-10 w-full rounded-md" />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const handleDeletePallet = (pallet: any, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the tab change
    setPalletToDelete(pallet);
    setIsDeleteModalOpen(true);
  };
  const handleNextPallet = () => {
    console.log('next pallet')
    const index = palletTypes.findIndex(p => p.id === Number(activePallet?.id));
    console.log(index, 'index of pallet')
    if (index < palletTypes.length - 1) {
      handlePalletChange(palletTypes[index + 1].id?.toString() || "")
    } else {
      showWarningToast('No more pallets')
    }
  }
  const handlePreviousPallet = () => {
    console.log('previous pallet')
    const index = palletTypes.findIndex(p => p.id === Number(activePallet?.id));
    console.log(index, 'index of pallet')
    if (index > 0) {
      handlePalletChange(palletTypes[index - 1].id?.toString() || "")
    } else {
      showWarningToast('No more pallets')
    }
  }
  const confirmDeletePallet = async () => {
    try {
      setDeleteLoading(true)
      if (isOffline && onOfflineSubmit) {
        await onOfflineSubmit({
          type: 'deletePallet',
          palletId: palletToDelete.id
        });
      } else {
        await createPalletDetails({
          type: 'delete',
          palletId: palletToDelete.id
        }).unwrap();
        dispatch(removePalletType(palletToDelete.id));
        showSuccessToast("Pallet deleted successfully");

        // Set active pallet to the first available pallet after deletion
        if (palletTypes.length > 1) {
          const remainingPallets = palletTypes.filter(p => p.id !== palletToDelete.id);
          if (remainingPallets.length > 0) {
            dispatch(setActivePallet(remainingPallets[0]));
          }
        } else {
          dispatch(setActivePallet(null));
        }

        refetchPallets();
      }
      setDeleteLoading(false)
    } catch (error) {
      setDeleteLoading(false)
      console.error("Error deleting pallet:", error);
      showErrorToast("Failed to delete pallet");
    } finally {
      setDeleteLoading(false)
      setIsDeleteModalOpen(false);
      setPalletToDelete(null);
    }
  };

  if (isFetching) {
    return (
      <div className="w-full flex h-[calc(100vh-140px)]">
        <div className="w-[200px] h-full overflow-y-auto bg-light-blue flex flex-col items-start justify-start pt-10">
          <div className="p-2 px-5">
            <Skeleton className="h-8 w-32 my-2" />
          </div>
          <Skeleton className="h-6 w-36 mx-5 my-3" />
          <div className="w-full px-5 space-y-4 mt-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-16 w-full rounded-md" />
            ))}
          </div>
          <div className="px-5 mt-5 w-full">
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        </div>

        <div className="flex-1 x-space py-8">
          <Skeleton className="h-8 w-40 mb-6" />

          <div className="w-full flex-center gap-4 mb-6">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex flex-col gap-2 w-full">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-10 w-full rounded-md" />
              </div>
            ))}
          </div>

          {/* Accordion Skeletons */}
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="mb-8 border rounded-md">
              <div className="p-4 border-b">
                <Skeleton className="h-6 w-48" />
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-6">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="flex flex-col gap-2">
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-10 w-full rounded-md" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <form
      onChange={handleFormChange}
      onSubmit={(event) => {
        // The Add Pallet dialog's own form submit also bubbles here through its portal.
        if (!event.currentTarget.contains(event.target as Node)) return;
        formik.handleSubmit(event);
      }}
      className="w-full overflow-y-hidden  "
    >
      <Tabs
        defaultValue={palletTypes[0]?.id?.toString() || ""}
        onValueChange={handlePalletChange}
        value={activePallet?.id?.toString() || ""}
        // value={activePallet?.id?.toString() || ""}
        className="w-full flex h-[calc(100vh-140px)] overflow-y-hidden"
      >
        {isNav ? (
          <TabsList className="w-[200px] z-50 h-full overflow-y-auto bg-light-blue flex flex-col items-start justify-start pt-10 relative">
            <ChevronsLeft onClick={() => setIsNav(!isNav)} className="cursor-pointer absolute top-2 right-2 text-blue" />
            <div className="p-2">
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2 bg-transparent border-none text-black text-base hover:bg-transparent">
                    <InfoIcon className="" />Legend
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[425px]">
                  <DialogHeader>
                    <DialogTitle>Field Types Legend</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="min-w-4 min-h-4 rounded-md bg-bgYellow border-2 border-yellow-200 shadow-sm"></div>
                      <span>Auto-calculated fields (editable)</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="min-w-4 min-h-4 rounded-md bg-white border-2 shadow-sm"></div>
                      <span>Data entry fields</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="min-w-4 min-h-4 rounded-md bg-gray-50 border-2 border-gray-200 shadow-sm"></div>
                      <span>Reference fields</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="min-w-4 min-h-4 rounded-md bg-selectBlue border-2 border-gray-200 shadow-sm"></div>
                      <span>Drop down list</span>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <p className="p-5 text-md text-black text-left font-semibold">Added Pallets</p>
            {palletTypes.length > 0 ? (
              palletTypes.map((pallet) => (
                <TabsTrigger
                  key={pallet.id}
                  value={pallet.id?.toString() || ""}
                  className="w-full justify-start px-5 py-4 text-left border-b-0 last:border-b-0 relative"
                >
                  <div className="flex flex-col gap-1">
                    <div>{pallet.variant}</div>
                    <div>{pallet.name}</div>
                  </div>
                  {!pathname.pathname.includes('offline') && (
                    <Button
                      onClick={(e) => handleDeletePallet(pallet, e)}
                      variant="ghost"
                      type="button"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 h-auto text-red-500 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 size={16} />
                    </Button>
                  )}
                </TabsTrigger>
              ))
            ) : (
              <div className="px-5 py-4 text-gray-500 italic">No pallets added yet</div>
            )}
            {!pathname.pathname.includes('offline') && <AddPalletDialog
              onAddPallet={handleAddPalletDialog}
              createPalletLoading={createPalletLoading}
              isOffline={isOffline}
            />}
          </TabsList>
        ) : (
          <TabsList className="w-[40px] relative z-50 h-full bg-light-blue flex flex-col items-start justify-start pt-10 overflow-y-auto">
            <ChevronsRight onClick={() => setIsNav(!isNav)} className="cursor-pointer absolute top-2 right-2 text-blue" />
          </TabsList>
        )}

        {palletTypes.length > 0 ? (
          palletTypes.map((pallet) => (
            <TabsContent
              value={pallet.id?.toString() || ""}
              key={pallet.id}
              className="h-full pb-[100px] overflow-y-auto bg-[#fcfcfc]"
            >
              {activePallet?.id === pallet.id && isPalletDetailsLoading ? (
                <AccordionSkeletons />
              ) : (
                <div className="w-full space-y-4 pb-20">
                  <h1 className="semi-bold-20 px-2 lg:px-auto lg:x-space pt-8"> Pallet Details</h1>
                  <div className="w-full flex-center gap-2 md:gap-4 px-2 lg:px-auto lg:x-space">
                    <div className="flex flex-col gap-2 w-full">
                      <div className=' font-medium'>Pallet Variant</div>
                      <Select
                        value={pallet.variant}
                        onValueChange={(value) => dispatch(updatePalletType({ ...pallet, variant: value }))}
                      >
                        <SelectTrigger className="rounded-md border border-border-style-1">
                          <SelectValue
                            placeholder="Pallet Types"
                            className="text-placeholder  placeholder:text-placeholder"
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {["Control 1", "Control 2", "Control 3", "Control 4", "Control 5", "Variable 1", "Variable 2", "Variable 3", "Variable 4", "Variable 5"].map((variant) => (
                            <SelectItem
                              key={variant}
                              className="capitalize"
                              value={variant}
                            >
                              {variant}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col gap-2 w-full">
                      <div className=' font-medium'>Pallet Name</div>
                      <Input
                        value={pallet.name}
                        onChange={(e) => dispatch(updatePalletType({ ...pallet, name: e.target.value }))}
                        placeholder="Pallet Name"
                        className="border border-border-style-1 rounded-md placeholder:text-placeholder placeholder-shown:regular-16"
                      />
                    </div>
                    {!isOffline && <div className="w-full flex flex-col gap-2">
                      <div className=' font-medium'>Shipping Modes</div>
                      <div className="h-10 flex gap-2 items-center flex-wrap overflow-y-auto border border-border-style-1 rounded-md p-2">
                        {shippingModes.map((mode) => (
                          <div key={mode.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`mode-${mode.id}`}
                              checked={pallet.shippingModes.find((spMode) => spMode.id === mode.id)?true:false}
                              // checked={pallet.shippingModes.find((spMode) => spMode.id === mode.id)}
                              onCheckedChange={(checked) => {
                                const updatedModes = checked
                                  ? [...pallet.shippingModes, mode]
                                  : pallet.shippingModes.filter((spMode) => spMode.id !== mode.id);
                                dispatch(updatePalletType({ ...pallet, shippingModes: updatedModes }));
                              }}
                            />
                            <label htmlFor={`mode-${mode.id}`} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                              Mode {mode.mode}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>}
                    <div className="flex flex-col gap-2 w-full">
                      <div className=' font-medium'>Number of Pallets</div>
                      <Input
                        value={pallet.numberOfPallets?.toString()}
                        onChange={(e) => dispatch(updatePalletType({ ...pallet, numberOfPallets: parseInt(e.target.value) }))}
                        placeholder="No Of Pallets"
                        type="number"
                        className="border border-border-style-1 h-10 rounded-md text-placeholder regular-16"
                      />
                    </div>
                    <Button
                      type="button"
                      disabled={createPalletLoading}
                      className="button-style text-md mt-auto h-10 bg-blue hover:bg-[#00336C] flex items-center gap-2 hover:text-white x-space"
                      onClick={() => handleUpdatePallet(pallet)}
                    >
                      Update {createPalletLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    </Button>
                  </div>
                  {accordion.map((item, i) => (
                    <AccordionForm
                      key={i}
                      title={item.title}
                      form={item.form}
                      tooltipContent={item.tooltipContent}
                      isActive={activeComponent === item.title}
                      ref={accordionRefs.current[item.title]}
                    />
                  ))}
                  <div className="w-full flex-row flex items-center justify-end gap-2 px-2 ml-auto w-fit lg:px-auto lg:x-space">
                    {activePallet?.id !== palletTypes[0]?.id && <Button onClick={() => handlePreviousPallet()} className="  bg-blue hover:bg-[#00336C]">
                      Previous pallet
                    </Button>}
                    {activePallet?.id !== palletTypes[palletTypes.length - 1]?.id && <Button onClick={() => handleNextPallet()} className="  bg-blue hover:bg-[#00336C]">
                      Next pallet
                    </Button>}
                  </div>
                </div>
              )}
            </TabsContent>
          ))
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl font-semibold text-gray-500 mb-4">No Pallets Added</div>
              <div className="text-blue font-semibold">
                Use the &quot;Add&quot; button on the left to create your first pallet
              </div>
            </div>
          </div>
        )}
      </Tabs>

      {/* Delete confirmation modal */}
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Pallet</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-red-600 font-medium">Warning: This action cannot be undone.</p>
            <p className="mt-2">
              Are you sure you want to delete this pallet? This will delete all associated data including media files, test data, and configurations.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsDeleteModalOpen(false)}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeletePallet}
              disabled={deleteLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteLoading ? "Deleting..." : "Delete Pallet"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {!isOffline && <Footer
        isLoading={createPalletDetailsLoading}
        layout
        onSubmit={async () => {
          handleSubmit(formik.values)
        }}
        hasUnsavedChanges={hasUnsavedChanges}
      />}
    </form>
  );
};
